import React, { useState, useEffect, useRef } from "react";
import PropType from "prop-types";
import { clearAllBodyScrollLocks } from "body-scroll-lock";
import useGatsbyAuth from "../../templates/user-account/hook/useGatsbyAuth";
import { exitIntentCookieName } from "./constants";
import { setCookie } from "../../utils/helper";
// eslint-disable-next-line import/no-cycle
import { EmailAcquisitionWidget } from "./Widget/EmailAcquisitionWidget";
// eslint-disable-next-line import/no-cycle
import { EmailAcquisitionEmbed } from "./Widget/EmailAcquisitionEmbed";

let cssEl;

const EmailAcquisitionConnector = ({ data, lang }) => {
	const [isWidgetOpen, setWidgetOpen] = useState(false);
	const [userEmail, setUserEmail] = useState("");
	const [userGateway, setUserGateway] = useState("");
	const [isRegistered, setRegistered] = useState(null); // States: 1 = Saved & Closed UI | 2 = Saved | null = Init
	const { loading } = useGatsbyAuth();
	const [exitIntentActive, setExitIntentActive] = useState(false);
	const exitIntentEnabled = useRef(data?.marketing?.exit !== undefined); // Setting to enable / disable exit intent feature
	const [isExitIntentSubmitted, setIsExitIntentSubmitted] = useState(false);

	const handleExitDismiss = () => {
		if (exitIntentActive && exitIntentEnabled.current) {
			let expiryDays = 7;
			if (typeof data?.marketing?.exit?.expiryDays === "number") {
				expiryDays = data.marketing.exit.expiryDays;
			}

			// Set cookie date
			const expiration = new Date();
			expiration.setUTCDate(expiration.getUTCDate() + expiryDays);

			const cookieDomain = window.location.hostname.replace(/^www\./, ".");

			setCookie(exitIntentCookieName, "true", "/", cookieDomain, expiration);
			setExitIntentActive(false);
			sessionStorage.removeItem(exitIntentCookieName);
			clearAllBodyScrollLocks();

			// Safari fix
			document.getElementsByTagName("body")[0].style.overflow = null;
		}
	};

	useEffect(() => {
		if (!loading) {
			/* Initialize if sessionStorage tracking is false */
			if (data?.id && isRegistered === null) {
				setRegistered(sessionStorage.getItem(`${data.id}-registration`));
			}

			// Set up Olark CSS injection  only for default widgets
			if (data?.type === "default") {
				cssEl = document.createElement("link");
				cssEl.id = "olark-css";
				cssEl.type = "text/css";
				cssEl.rel = "stylesheet";
				cssEl.href = "/assets/email-signup/olark.css";
				document.head.appendChild(cssEl);
			}
		}

		return () => {
			if (cssEl?.parentNode !== null && !loading && data?.type === "default") {
				cssEl.parentNode.removeChild(cssEl);
			}
		};
	}, [data, isRegistered, loading]);

	if (data) {
		if (data?.type === "inline" || data?.type === "split") {
			return (
				<EmailAcquisitionEmbed
					config={data}
					isWidgetOpen={isWidgetOpen}
					setWidgetOpen={setWidgetOpen}
					lang={lang}
					userEmail={userEmail}
					setUserEmail={setUserEmail}
					userGateway={userGateway}
					setUserGateway={setUserGateway}
					setRegistered={setRegistered}
					isRegistered={isRegistered}
					setExitIntentActive={setExitIntentActive}
					exitIntentActive={exitIntentActive}
					exitIntentEnabled={exitIntentEnabled?.current}
					handleExitDismiss={handleExitDismiss}
					isExitIntentSubmitted={isExitIntentSubmitted}
					setIsExitIntentSubmitted={setIsExitIntentSubmitted}
				/>
			);
		}

		if (data?.type === "default" || data?.type === "exit") {
			return (
				<>
					{(isRegistered === null || isRegistered === "2") && (
						<>
							<EmailAcquisitionWidget
								config={data}
								mode="desktop"
								isWidgetOpen={isWidgetOpen}
								setWidgetOpen={setWidgetOpen}
								lang={lang}
								userEmail={userEmail}
								setUserEmail={setUserEmail}
								userGateway={userGateway}
								setUserGateway={setUserGateway}
								setRegistered={setRegistered}
								isRegistered={isRegistered}
								setExitIntentActive={setExitIntentActive}
								exitIntentActive={exitIntentActive}
								exitIntentEnabled={exitIntentEnabled?.current}
								handleExitDismiss={handleExitDismiss}
								isExitIntentSubmitted={isExitIntentSubmitted}
								setIsExitIntentSubmitted={setIsExitIntentSubmitted}
							/>
							<EmailAcquisitionWidget
								config={data}
								mode="mobile"
								isWidgetOpen={isWidgetOpen}
								setWidgetOpen={setWidgetOpen}
								lang={lang}
								userEmail={userEmail}
								setUserEmail={setUserEmail}
								userGateway={userGateway}
								setUserGateway={setUserGateway}
								setRegistered={setRegistered}
								isRegistered={isRegistered}
								setExitIntentActive={setExitIntentActive}
								exitIntentActive={exitIntentActive}
								exitIntentEnabled={exitIntentEnabled?.current}
								handleExitDismiss={handleExitDismiss}
								isExitIntentSubmitted={isExitIntentSubmitted}
								setIsExitIntentSubmitted={setIsExitIntentSubmitted}
							/>
						</>
					)}
				</>
			);
		}
	}

	return null;
};

EmailAcquisitionConnector.propTypes = {
	data: PropType.object,
	lang: PropType.oneOf(["en", "fr"]).isRequired,
};

EmailAcquisitionConnector.defaultProps = {
	data: null,
};

export default EmailAcquisitionConnector;
export { EmailAcquisitionConnector };
